import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Button,
  Form,
  Modal,
  message
} from 'antd'
import 'antd/dist/antd.css'
import './NanoBananAi.scss'
import './AiChat.scss'
import { ImagePreview } from '../../../../../../../../../components'
import { api } from '../../../../../../../../../services'
import { useSelector } from 'react-redux'
// import { GoogleGenAI } from '@google/genai';
import {
  FiAlertTriangle,
  FiEdit3,
  FiMessageSquare,
  FiRefreshCw
} from 'react-icons/fi'
import { PopupMedia } from '../../../../../../../../Library/internal'
import _ from 'lodash'
import imageCompression from 'browser-image-compression'
import { useTranslation } from 'react-i18next'
import AspectRatioSelector from '../AiImages/AspectRatioSelector'
import PixelSearchModal from './PixelSearchModal'
import AiChat from './AiChat'
import AiImagesForm from './AiImagesForm'
// const GEMINI_API_KEY = process.env.REACT_APP_NANO_BANANA_API_KEY;
// const ai = new GoogleGenAI({ apiKey: GEMINI_API_KEY });

const NanoBananAi = ({
  form,
  onImage,
  selectedImage,
  isUploading,
  isBackground = false,
  isCover = false,
  pageBuilder = {},
  courseId = null,
  userId = null,
  orgId = null,
}) => {
  const { getFieldDecorator, setFieldsValue } = form
  const [promptText, setPromptText] = useState('')
  const [promptTitle, setPromptTitle] = useState('')
  const [includeText, setIncludeText] = useState(false)
  const [mediaLoading] = useState(false)
  const [generationStatus, setGenerationStatus] = useState({
    status: 'idle',
    percentage: 0,
    isVariation: false
  })
  const [currentGeneratedImage, setCurrentGeneratedImage] = useState({
    info: '',
    url: ''
  })
  const [generationError, setGenerationError] = useState(null)
  const [refImages, setRefImages] = useState([]) // {id, preview, base64, mime, sizeMB, mediaId}
  const endOfPagesRef = useRef(null)
  const [activeTab, setActiveTab] = useState(isCover ? 'text-to-image' : 'image-edit') // 'image-edit' | 'text-to-image'
  const [showPromptHelper, setShowPromptHelper] = useState(false)
  const [showPixelSearchModal, setShowPixelSearchModal] = useState(false)
  const [showMediaLibrary, setShowMediaLibrary] = useState(false)

  const { t } = useTranslation()
  const user = useSelector(state => state.user)
  const isSuperAdmin = !!(
    user?.info?.is_super_admin || user?.info?.isSuperAdmin
  )
  const [aiCreditStatus, setAiCreditStatus] = useState(isSuperAdmin)

  useEffect(() => {
    if (isSuperAdmin) {
      setAiCreditStatus(true)
      return
    }

    const checkAiCreditStatus = async () => {
      try {
        const response = await api.aiCredits.checkAiCreditsStatus("image")
        if (response?.data?.allowed) {
          setAiCreditStatus(true)
        } else {
          setAiCreditStatus(false)
          message.error(t('aiusage:you_do_not_have_enough_ai_credits') + " images")
        }
      } catch (error) {
        message.error(t('aiusage:error_fetching_ai_credit_status'))
      }
    }
    checkAiCreditStatus()
  }, [isSuperAdmin, t])


  useEffect(() => {
    if (
      generationStatus.status === 'waiting-to-start' ||
      generationStatus.status === 'running' ||
      generationStatus.status === 'complete'
    ) {
      goToEndOfPages()
    }
  }, [generationStatus.status])

  useEffect(() => {
    const { contentId, content } = pageBuilder
    if (contentId && content) {
      handleMediaSelect({ id: contentId, url: content })
    }
  }, [pageBuilder])

  useEffect(() => {
    if (
      generationStatus.status === 'waiting-to-start' ||
      generationStatus.status === 'running' ||
      generationStatus.status === 'complete'
    ) {
      goToEndOfPages()
    }
  }, [generationStatus.status])

  // Trigger validation when Image Edit tab is active and no images are present
  useEffect(() => {
    if (activeTab === 'image-edit' && refImages.length === 0) {
      setTimeout(() => {
        form.validateFields(['referenceImages'], () => { })
      }, 100)
    }
  }, [activeTab, refImages.length, form])

  const goToEndOfPages = () => {
    if (endOfPagesRef.current) {
      endOfPagesRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }

  const normalizeBase64 = base64 => {
    if (!base64 || typeof base64 !== 'string') return ''
    return base64.includes(',') ? base64.split(',').pop() : base64
  }

  const base64ToBlob = (base64Data, contentType = 'image/jpeg') => {
    const cleanBase64 = normalizeBase64(base64Data)
    const byteChars = atob(cleanBase64)
    const byteNumbers = new Array(byteChars.length)
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    return new Blob([byteArray], { type: contentType })
  }

  const blobToDataUrl = blob =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })

  const getReferencePreview = image =>
    image?.preview ||
    image?.url ||
    image?.link ||
    image?.src?.large ||
    image?.src?.medium ||
    image?.src?.original ||
    ''

  const getReferenceSizeMB = image => {
    const size = Number(image?.file_size || image?.size)
    if (!Number.isNaN(size) && size > 0) {
      return (size / (1024 * 1024)).toFixed(2)
    }
    return image?.sizeMB || '0.00'
  }

  const createReferenceImage = (image, source = 'external') => {
    const preview = getReferencePreview(image)
    if (!preview) return null

    return {
      id: Date.now() + Math.random(),
      mediaId: source === 'media' ? image?.id : null,
      source,
      preview,
      base64: image?.base64 ? normalizeBase64(image.base64) : '',
      mime: image?.mime || image?.type || 'image/jpeg',
      sizeMB: getReferenceSizeMB(image)
    }
  }

  const setReferenceImagesValue = images => {
    setRefImages(images)

    setTimeout(() => {
      form.setFields({
        referenceImages: {
          value: images.length,
          errors: null
        }
      })
    }, 0)
  }

  const resolveReferenceImageData = async image => {
    if (image?.base64) return image

    let base64 = ''
    let blob = null
    const mime = image?.mime || image?.type || 'image/jpeg'

    if (image?.mediaId) {
      const bufferRes = await api.library.getLibraryBuffer(image.mediaId)
      const bufferData =
        typeof bufferRes?.data === 'string'
          ? bufferRes.data
          : bufferRes?.data?.base64 || bufferRes?.data?.data

      if (bufferData) {
        base64 = normalizeBase64(bufferData)
        blob = base64ToBlob(base64, bufferRes?.data?.type || mime)
      }
    }

    if (!base64 && image?.preview) {
      const response = await fetch(image.preview)
      if (!response.ok) {
        throw new Error(`Unable to fetch reference image: ${response.status}`)
      }
      blob = await response.blob()
      const dataUrl = await blobToDataUrl(blob)
      base64 = normalizeBase64(dataUrl)
    }

    if (!base64) {
      throw new Error('Reference image has no readable data')
    }

    return {
      ...image,
      base64,
      mime: blob?.type || mime,
      sizeMB: blob?.size
        ? (blob.size / (1024 * 1024)).toFixed(2)
        : image?.sizeMB || '0.00'
    }
  }

  const addReferenceImage = (image, source = 'external') => {
    if (refImages.length >= 9) {
      message.warning(t('v5:max_reference_images_allowed'))
      return
    }

    const newRefImage = createReferenceImage(image, source)
    if (!newRefImage) return

    const newRefImages = [...refImages, newRefImage]
    setReferenceImagesValue(newRefImages)
  }

  const stringifyErrorValue = value => {
    if (!value) return ''
    if (typeof value === 'string') return value
    if (value.message && typeof value.message === 'string') return value.message
    return ''
  }

  const formatFinishReason = reason => {
    if (!reason) return ''

    const reasonText = String(reason)
      .replace('FinishReason.', '')
      .replace(/_/g, ' ')
      .toLowerCase()

    return reasonText.charAt(0).toUpperCase() + reasonText.slice(1)
  }

  const normalizeFinishReason = reason =>
    String(reason || '')
      .replace('FinishReason.', '')
      .toUpperCase()

  const isSuccessfulFinishReason = reason => {
    const normalizedReason = normalizeFinishReason(reason)
    return normalizedReason === 'STOP' || normalizedReason === 'SUCCESS'
  }

  const buildGenerationError = ({ messageText = '', finishReason = '' } = {}) => {
    const normalizedMessage = messageText.toLowerCase()
    const normalizedReason = String(finishReason).toLowerCase()
    const blockedBySafety =
      normalizedReason.includes('prohibited') ||
      normalizedReason.includes('safety') ||
      normalizedMessage.includes('prohibited') ||
      normalizedMessage.includes('safety')

    if (blockedBySafety) {
      return {
        title: 'Image request was blocked',
        description:
          'The generation service rejected this prompt or reference image. Try changing the prompt, removing sensitive details, or choosing a different reference image.',
        reason: 'Safety filter'
      }
    }

    const genericFailure =
      !messageText || normalizedMessage === 'image generation failed'

    return {
      title: 'Image generation failed',
      description: genericFailure
        ? 'The image service could not finish this request. Try again, or adjust the prompt or reference image.'
        : messageText,
      reason: formatFinishReason(finishReason)
    }
  }

  const getApiGenerationFailure = payload => {
    if (!payload || typeof payload !== 'object') return null

    const source =
      payload.data && typeof payload.data === 'object' ? payload.data : payload
    const errorText =
      stringifyErrorValue(source.errorMessage) ||
      stringifyErrorValue(source.error) ||
      stringifyErrorValue(source.message) ||
      stringifyErrorValue(payload.errorMessage) ||
      stringifyErrorValue(payload.error)
    const finishReason =
      source.finish_reason ||
      source.finishReason ||
      payload.finish_reason ||
      payload.finishReason ||
      ''

    if (!errorText && (!finishReason || isSuccessfulFinishReason(finishReason))) {
      return null
    }

    return buildGenerationError({
      messageText: errorText,
      finishReason
    })
  }

  const getGenerationErrorContent = error => {
    const payload =
      error?.response?.data || error?.data || error?.error || error
    const apiFailure = getApiGenerationFailure(payload)

    if (apiFailure) return apiFailure

    return buildGenerationError({
      messageText:
        stringifyErrorValue(error?.error?.message) ||
        stringifyErrorValue(error?.message)
    })
  }

  const handleGenerationFailure = error => {
    const nextError = getGenerationErrorContent(error)
    setGenerationError(nextError)
    message.error(nextError.title)
    setGenerationStatus({
      status: 'error',
      percentage: 0,
      isVariation: false
    })
  }

  const focusPromptInput = () => {
    setShowPromptHelper(false)
    setTimeout(() => {
      const promptInput = document.getElementById('promptInput')
      const promptTextArea = document.querySelector('.ai-images-form textarea')

      if (promptInput) {
        promptInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }

      if (promptTextArea) {
        promptTextArea.focus()
      }
    }, 0)
  }

  const handleGenerateImage = (prompt = '') => {
    setGenerationError(null)
    // Always validate form fields first
    form.validateFields((errors, values) => {
      if (errors) {
        // Log errors for debugging
        console.log('Validation errors:', errors)
        return
      }

      // Additional check for Image Edit mode
      if (activeTab === 'image-edit' && refImages.length === 0) {
        message.error(t('v5:please_upload_reference_image'))
        return
      }

      if (!prompt && !promptText) {
        return
      }

      startGeneration(prompt)
    })
  }

  const generateImageWithGemini = async (contents, options = {}) => {
    const { aspectRatio, processedImages = [] } = options
    try {
      setGenerationError(null)
      setGenerationStatus({
        status: 'running',
        percentage: 0,
        isVariation: false
      })

      // Build the new payload format
      const payload = {
        generate_infographic: false,
        user_prompt: contents[0]?.parts?.[0]?.text || '',
        aspect_ratio: aspectRatio || '1:1',
        user_id: userId,
        org_id: orgId,
        ...(courseId && { type: 'course', course_id: Number(courseId) }),
      }

      // Add inline_images if there are reference images
      if (processedImages && processedImages.length > 0) {
        payload.inline_images = processedImages.map(img => ({
          data: img.base64,
          mime_type: img.mime || 'image/png'
        }))
      }

      const response = await api.imageGeneration.generateNanoBananaImage(payload)

      if (response?.data) {
        let imageData = null

        // Helper to extract metadata from a JSON object
        const extractMetadata = (obj) => {
          // Check top-level or nested 'data' object
          const source = obj.key || obj.url ? obj : (obj.data?.key || obj.data?.url ? obj.data : null)
          if (source) {
            const filename = source.filename || (source.key ? source.key.split('/').pop() : `nano-ai-${Date.now()}.jpg`)
            return {
              info: '',
              url: source.url || `https://${process.env.REACT_APP_S3_BUCKET}.s3.amazonaws.com/${source.key}`,
              key: source.key,
              filename: filename,
              type: source.mime_type || source.type || 'image/jpeg'
            }
          }
          return null
        }

        // 1. Handle direct JSON response (parsed by axios)
        imageData = extractMetadata(response.data)
        const responseFailure = getApiGenerationFailure(response.data)

        if (!imageData && responseFailure) {
          handleGenerationFailure(response.data)
          return
        }

        // 2. Handle Blob response
        if (!imageData && (response.data instanceof Blob || (response.data.type && typeof response.data.size === 'number' && !response.data.body))) {
          const blob = response.data
          if (blob.type === 'application/json' || blob.size < 2000) { // Small blobs might be JSON errors/metadata
            try {
              const text = await new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = () => resolve(reader.result)
                reader.onerror = reject
                reader.readAsText(blob)
              })
              const json = JSON.parse(text)
              imageData = extractMetadata(json)
              if (!imageData && json.errorMessage) {
                handleGenerationFailure(json)
                return
              }
            } catch (e) {
              // Not JSON, continue to blob handling
            }
          }

          if (!imageData) {
            // Standard image blob
            const result = await new Promise((resolve, reject) => {
              const reader = new FileReader()
              reader.onloadend = () => resolve(reader.result)
              reader.onerror = reject
              reader.readAsDataURL(blob)
            })
            imageData = { info: '', url: result }
          }
        }

        // 3. Handle old format (JSON with body/errorMessage)
        if (!imageData && (response.data.body || response.data.errorMessage)) {
          const { body, headers, errorMessage } = response.data
          if (errorMessage) {
            handleGenerationFailure(response.data)
            return
          }
          const contentType = headers?.['Content-Type'] || 'image/png'
          imageData = { info: '', url: `data:${contentType};base64,${body}` }
        }

        if (imageData) {
          setGenerationError(null)
          setGenerationStatus({
            status: 'complete',
            percentage: 100,
            isVariation: false
          })
          setCurrentGeneratedImage(imageData)
        } else {
          console.error('Unexpected response format:', response.data)
          throw new Error('Unexpected response format')
        }
        return
      }
    } catch (error) {
      handleGenerationFailure(error)
    }
  }

  const startGeneration = async value => {
    let prompt = value || promptText
    if (promptTitle) {
      prompt = `${prompt} with text "${promptTitle}"`
    }

    form.validateFields(async (errors, values) => {
      if (errors) {
        console.log('Form validation errors:', errors)
        return
      }

      // Double-check for image edit mode
      if (activeTab === 'image-edit' && refImages.length === 0) {
        message.error(t('v5:please_upload_reference_image'))
        return
      }

      let finalPrompt = prompt

      const selectedAspectRatio = isCover ? '16:9' : values.aspectRatio

      // Resolve selected library/CDN images only after the user starts generation.
      let processedImages = refImages
      if (activeTab === 'image-edit' && refImages && refImages.length) {
        setGenerationStatus({
          status: 'waiting-to-start',
          percentage: 0,
          isVariation: false
        })

        const resolvedImages = await Promise.all(
          refImages.map(async img => {
            try {
              return await resolveReferenceImageData(img)
            } catch (err) {
              console.error('Failed to prepare reference image:', err)
              return null
            }
          })
        )

        processedImages = resolvedImages.filter(Boolean)

        if (processedImages.length) {
          setRefImages(prev =>
            prev.map(img => {
              const resolvedImage = processedImages.find(
                processed => processed.id === img.id
              )
              return resolvedImage ? { ...img, ...resolvedImage } : img
            })
          )
        } else {
          setGenerationStatus({
            status: 'idle',
            percentage: 0,
            isVariation: false
          })
          return
        }

        // Compress images to keep total under ~5 MB
        const totalTargetMB = 3 // total size budget
        const perImageTargetMB = totalTargetMB / 9 // fixed divisor for safety

        processedImages = await Promise.all(
          processedImages.map(async (img, index) => {
            try {
              const dataUrl = img.base64.startsWith('data:')
                ? img.base64
                : `data:${img.mime || 'image/png'};base64,${img.base64}`

              const fileFromDataUrl = await imageCompression.getFilefromDataUrl(
                dataUrl,
                `ref-${index + 1}.${(img.mime || 'image/png').split('/')[1] ||
                'png'}`
              )

              const compressedFile = await imageCompression(fileFromDataUrl, {
                maxSizeMB: perImageTargetMB,
                maxWidthOrHeight: 800,
                useWebWorker: true
              })

              const compressedBase64 = await imageCompression.getDataUrlFromFile(
                compressedFile
              )
              return {
                base64: compressedBase64.split(',')[1],
                mime: compressedFile.type || img.mime || 'image/png'
              }
            } catch (err) {
              console.error('Image compression failed:', err)
              return img // fallback to original if compression fails
            }
          })
        )
      }

      const contents = [
        {
          role: 'user',
          parts: [
            { text: finalPrompt },
            ...(activeTab === 'image-edit' &&
              processedImages &&
              processedImages.length
              ? processedImages.map(img => ({
                inlineData: {
                  data: img.base64,
                  mimeType: img.mime || 'image/png'
                },
                mediaResolution: {
                  level: 'media_resolution_high'
                }
              }))
              : [])
          ]
        }
      ]

      generateImageWithGemini(contents, {
        aspectRatio: selectedAspectRatio,
        processedImages: activeTab === 'image-edit' ? processedImages : []
      })
    })
  }

  const selectImage = () => {
    const linkToUse = currentGeneratedImage?.url
    if (!linkToUse) return
    if (selectedImage && selectedImage.link === linkToUse) {
      return
    }

    onImage({
      link: linkToUse,
      id: generate7DigitRandomInteger(),
      data: currentGeneratedImage || null
    }, { autoInsert: true })
  }

  const editGeneratedImage = () => {
    const linkToUse = currentGeneratedImage?.url
    if (!linkToUse) return

    const generatedReference = createReferenceImage(
      {
        ...currentGeneratedImage,
        url: linkToUse
      },
      'generated'
    )

    if (!generatedReference) return

    setActiveTab('image-edit')
    setGenerationError(null)
    setGenerationStatus({
      status: 'idle',
      percentage: 0,
      isVariation: false
    })
    setCurrentGeneratedImage({ info: '', url: '' })
    setReferenceImagesValue([generatedReference])
    setPromptText('')
    setPromptTitle('')
    setFieldsValue({ prompt: '', title: '' })
    message.success('Generated image added as an editable reference')
    focusPromptInput()
  }

  function generate7DigitRandomInteger() {
    const min = 1000000
    const max = 9999999
    return Math.floor(Math.random() * (max - min + 1)) + min
  }

  const isImageBeingGenerated = useMemo(() => {
    return (
      generationStatus.status !== 'idle' &&
      generationStatus.status !== 'error' &&
      generationStatus.status !== 'complete'
    )
  }, [generationStatus])

  const handleGenerateFromAi = prompt => {
    setShowPromptHelper(false)
    setPromptText(prompt)
    setFieldsValue({ prompt: prompt })

    const promptInput = document.getElementById('promptInput')
    if (promptInput) {
      promptInput.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handlePixelImageSelect = image => {
    addReferenceImage(image)
  }

  const handleMediaSelect = image => {
    addReferenceImage(image, 'media')
    setShowMediaLibrary(false)
  }

  const handleInsertData = (privateMedia, publicMedia, orgMedia) => {
    let tempData = []
    if (privateMedia) {
      const { rows: privateData } = privateMedia
      privateData.forEach(data => {
        tempData.push(data)
      })
    }

    if (publicMedia) {
      const { rows: publicData } = publicMedia
      publicData.forEach(publicData => {
        tempData.push(publicData)
      })
    }

    if (orgMedia) {
      const { rows: orgData } = orgMedia
      orgData.forEach(orgData => {
        tempData.push(orgData)
      })
    }

    const item = _.head(tempData.filter(value => value.isSelected))
    if (!item) return

    handleMediaSelect({
      ...item,
      url: item.link || item.url
    })
  }

  const hasGenerationError = generationStatus.status === 'error' && generationError

  return (
    <div className="nbai-container">
      <div className="nbai-left">
        <div className="nbai-card">
          <div className="nbai-card-header">
            <div className="nbai-form-header">
              <div className="nbai-form-sub">
                ⚡ {t('v4:ai_image_generation')}
              </div>
              <Button
                size="small"
                type="link"
                className="nbai-link-btn"
                onClick={() => setShowPromptHelper(prev => !prev)}
              >
                <FiMessageSquare />
                {showPromptHelper
                  ? t('v5:hide_prompt_helper')
                  : t('v5:prompt_helper')}
              </Button>
            </div>
          </div>
          <div className="nbai-tabs">
            <div
              className={`nbai-tab ${activeTab === 'image-edit' ? 'nbai-tab--active' : ''
                }`}
              onClick={() => {
                setActiveTab('image-edit')
                // Clear any existing validation errors first, then validate
                form.resetFields(['referenceImages'])
                setTimeout(() => {
                  if (refImages.length === 0) {
                    form.validateFields(['referenceImages'], () => { })
                  }
                }, 100)
              }}
            >
              {t('v4:image_edit')}
            </div>
            <div
              className={`nbai-tab ${activeTab === 'text-to-image' ? 'nbai-tab--active' : ''
                }`}
              onClick={() => setActiveTab('text-to-image')}
            >
              {t('v4:text_to_image')}
            </div>
          </div>
          <div className="nbai-section">
            <div className="nbai-uploader">
              <AiImagesForm
                aiCreditStatus={aiCreditStatus}
                isCover={isCover}
                isUploading={isUploading}
                mediaLoading={mediaLoading}
                form={form}
                getFieldDecorator={getFieldDecorator}
                promptText={promptText}
                isImageBeingGenerated={isImageBeingGenerated}
                handleGenerateImage={handleGenerateImage}
                setPromptText={setPromptText}
                promptTitle={promptTitle}
                setPromptTitle={setPromptTitle}
                generationStatus={generationStatus}
                includeText={includeText}
                setIncludeText={setIncludeText}
                isBackground={isBackground}
                refImages={refImages}
                setRefImages={setRefImages}
                showUploader={activeTab === 'image-edit'}
                showPromptHelper={showPromptHelper}
                onTogglePromptHelper={() => setShowPromptHelper(prev => !prev)}
                setShowMediaLibrary={() => setShowMediaLibrary(prev => !prev)}
                onOpenPixelSearch={() => setShowPixelSearchModal(true)}
                activeTab={activeTab}
                promptHelperNode={
                  showPromptHelper ? (
                    <AiChat
                      generateImage={handleGenerateFromAi}
                      isImageBeingGenerated={isImageBeingGenerated}
                      autoStart={showPromptHelper}
                    />
                  ) : null
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="nbai-right">
        <div className="nbai-card">
          <div className="nbai-card-header">{t('v4:output_gallary')}</div>
          <div className="nbai-output">
            {!currentGeneratedImage.url &&
              !hasGenerationError &&
              !isImageBeingGenerated && (
                <div className="nbai-output-empty">
                  <div className="nbai-output-empty-title">
                    {t('v4:instant_generation')}
                  </div>
                  <div className="nbai-output-empty-sub">
                    {t('v4:prompt_and_unleash_the_power')}
                  </div>
                </div>
              )}

            {hasGenerationError && (
              <div
                className="nbai-output-error"
                role="alert"
                aria-live="assertive"
              >
                <div className="nbai-error-icon" aria-hidden="true">
                  <FiAlertTriangle />
                </div>
                <div className="nbai-error-content">
                  <div className="nbai-error-eyebrow">Generation stopped</div>
                  <h3>{generationError.title}</h3>
                  <p>{generationError.description}</p>
                  {generationError.reason && (
                    <div className="nbai-error-reason">
                      <span>Reason</span>
                      {generationError.reason}
                    </div>
                  )}
                  <div className="nbai-error-actions">
                    <Button type="primary" onClick={() => handleGenerateImage()}>
                      <FiRefreshCw />
                      Try again
                    </Button>
                    <Button onClick={focusPromptInput}>
                      <FiEdit3 />
                      Edit prompt
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {currentGeneratedImage.url && !hasGenerationError && (
              <div className="nbai-output-preview">
                <ImagePreview
                  src={currentGeneratedImage.url}
                  alt={t('v5:generated_image_alt')}
                />
                <div className="nbai-output-actions">
                  <Button onClick={editGeneratedImage} disabled={isUploading}>
                    <FiEdit3 />
                    Edit This Image
                  </Button>
                  <Button
                    type="primary"
                    onClick={selectImage}
                    loading={isUploading}
                  >
                    {t('v4:use_this_image')}
                  </Button>
                </div>
              </div>
            )}

            {(generationStatus.status === 'running' ||
              generationStatus.status === 'waiting-to-start') && (
                <div className="nbai-loading-overlay">
                  <div className="nbai-loader">
                    <div className="nbai-ring" />
                    <h3>{t('v5:processing_your_request')}</h3>
                    <p>
                      {generationStatus.isVariation
                        ? t('v5:generating_variations_in_progress')
                        : t('v5:lightning_fast_generation_in_progress')}
                    </p>
                    <div className="nbai-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}

            {generationStatus.status === 'error' && !generationError && (
              <div className="nbai-output-error" role="alert">
                <div className="nbai-error-icon" aria-hidden="true">
                  <FiAlertTriangle />
                </div>
                <div className="nbai-error-content">
                  <h3>{t('v4:error_generating')}</h3>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="nbai-tips">
          <div className="nbai-card">
            <div className="nbai-card-header">{t('v4:generation_tips')}</div>
            <div className="nbai-card-body">
              {t('v4:detailed_descriptions_for_more_accurate_results')}
            </div>
          </div>
        </div>
      </div>
      <div ref={endOfPagesRef} />
      <Modal
        visible={showMediaLibrary}
        onCancel={() => setShowMediaLibrary(false)}
        footer={null}
        width={'80%'}
        centered
      >
        <div className="wrap_modal_tabs">
          <PopupMedia
            isPageBuilder
            handleInsertData={handleInsertData}
            types="images"
          />
        </div>
      </Modal>
      <PixelSearchModal
        visible={showPixelSearchModal}
        onClose={() => setShowPixelSearchModal(false)}
        onImageSelect={handlePixelImageSelect}
      />
    </div>
  )
}

export default Form.create()(NanoBananAi)
